/**
 * SetupSheetPreview: dedicated page for the export flow (PRD §14.7).
 * A separate route rather than an in-editor modal, so the browser's
 * print-to-PDF can reliably target just this content (see exportPdf.ts
 * and index.css's @media print rules for why a dedicated page, not a
 * modal, is the right call here).
 *
 * New: "Needs Attention" toggle (Priority 4) — lets a coordinator suppress
 * the validation warnings section from the printed sheet without affecting
 * the editor validation. The choice persists via useSaveLayout.
 */

import { useParams, useNavigate } from 'react-router-dom';
import { useWedding } from '../api/weddings';
import { useLayout, useSaveLayout } from '../api/layouts';
import { useRoomDefinitions } from '../api/roomDefinitions';
import { useObjectCatalog, useInventoryLimits } from '../api/inventoryLimits';
import { validateLayout } from '../domain/validation/validateLayout';
import { SetupSheetDocument } from '../components/export/SetupSheetDocument';
import { triggerPrint } from '../components/export/exportPdf';
import { useState } from 'react';

export function SetupSheetPreview() {
  const { weddingId } = useParams<{ weddingId: string }>();
  const navigate = useNavigate();

  const { data: wedding, isLoading: weddingLoading } = useWedding(weddingId);
  const { data: layout, isLoading: layoutLoading } = useLayout(weddingId);
  const { data: rooms } = useRoomDefinitions();
  const { data: catalog } = useObjectCatalog();
  const { data: inventoryLimits } = useInventoryLimits();
  const saveLayout = useSaveLayout();

  // Local state for the toggle, initialized from the persisted layout flag.
  // Using local state so the UI responds immediately; we persist on toggle.
  const [showWarnings, setShowWarnings] = useState<boolean | null>(null);

  const room = rooms?.find((r) => r.room === wedding?.room);

  if (weddingLoading || layoutLoading || !catalog || !rooms || !inventoryLimits) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="font-body text-ink-400">{'Loading\u2026'}</p>
      </div>
    );
  }

  if (!wedding || !layout || !room) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3">
        <p className="font-body text-ink-500">
          {'Nothing to export yet \u2014 generate a layout first.'}
        </p>
        <button onClick={() => navigate(-1)} className="font-body text-evergreen-600 underline">
          Go back
        </button>
      </div>
    );
  }

  // Resolve the effective showWarnings value: prefer local state if set,
  // fall back to the persisted layout flag.
  const persistedShowWarnings = layout.showWarningsOnSetupSheet;
  const effectiveShowWarnings = showWarnings !== null ? showWarnings : persistedShowWarnings;

  function handleToggleWarnings() {
    const next = !effectiveShowWarnings;
    setShowWarnings(next);
    // Persist immediately so the editor reflects the change too
    if (weddingId) {
      saveLayout.mutate({
        weddingId,
        objects: layout!.objects,
        showWarningsOnSetupSheet: next,
      });
    }
  }

  const allIssues = validateLayout(layout.objects, {
    roomDefinition: room,
    objectCatalog: catalog,
    inventoryLimits,
    guestCount: wedding.guestCount,
    layoutStyle: wedding.layoutStyle,
  });
  const criticalIssues = allIssues.filter((i) => i.severity === 'critical');
  const meaningfulIssues = allIssues.filter((i) => i.severity === 'critical' || i.severity === 'warning');

  return (
    <div className="min-h-screen bg-ivory-200 py-8">
      {/* Toolbar: hidden entirely when printing, via data-print="hide". */}
      <div
        data-print="hide"
        className="mx-auto mb-6 flex max-w-3xl items-center justify-between px-4"
      >
        <button
          onClick={() => navigate(-1)}
          className="font-body text-sm text-ink-500 hover:text-ink-700"
        >
          {'\u2190'} Back to editor
        </button>
        <div className="flex items-center gap-3">
          {/* Needs Attention toggle — allows suppressing validation section from print */}
          <label className="flex cursor-pointer items-center gap-2">
            <span className="font-body text-sm text-ink-500">
              Show &ldquo;Needs Attention&rdquo;
            </span>
            <button
              role="switch"
              aria-checked={effectiveShowWarnings}
              onClick={handleToggleWarnings}
              className={`relative h-6 w-11 rounded-full transition-colors ${
                effectiveShowWarnings ? 'bg-evergreen-600' : 'bg-ivory-400'
              }`}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                  effectiveShowWarnings ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </label>

          <button
            onClick={triggerPrint}
            className="rounded bg-evergreen-600 px-4 py-2 font-body text-sm font-medium text-ivory-50 hover:bg-evergreen-700"
          >
            Print / Save as PDF
          </button>
        </div>
      </div>

      {meaningfulIssues.length > 0 && (
        <div
          data-print="hide"
          className={
            criticalIssues.length > 0
              ? 'mx-auto mb-6 max-w-3xl rounded-lg border border-danger/30 bg-danger/5 px-4 py-3'
              : 'mx-auto mb-6 max-w-3xl rounded-lg border border-warning/30 bg-warning/5 px-4 py-3'
          }
        >
          <p className={`font-body text-sm font-medium ${criticalIssues.length > 0 ? 'text-danger' : 'text-warning'}`}>
            {'\u26A0'}{' '}
            {criticalIssues.length > 0
              ? `${criticalIssues.length} thing${criticalIssues.length === 1 ? '' : 's'} here ${criticalIssues.length === 1 ? 'needs' : 'need'} a look before this goes out`
              : `${meaningfulIssues.length} thing${meaningfulIssues.length === 1 ? '' : 's'} worth a quick check`}
          </p>
          <p className="mt-1 font-body text-xs text-ink-500">
            You can still print or share it as-is{criticalIssues.length > 0 ? ', but we\u2019d double-check this one first' : ''}.
            These also appear at the bottom of the printed sheet.
          </p>
        </div>
      )}

      {/* The actual printable content. */}
      <div data-print="content">
        <SetupSheetDocument
          wedding={wedding}
          room={room}
          objects={layout.objects}
          catalog={catalog}
          warnings={allIssues}
          showWarningsOnSetupSheet={effectiveShowWarnings}
        />
      </div>
    </div>
  );
}
